import { useState, useEffect } from "react";
import { Upload, Trash2, Star } from "lucide-react";
import {
  FiEye,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useHouseDesign } from "../useHook";
import { useBuyPlans } from "../useHook";
import Swal from "sweetalert2";
import SimilarDesignModal from "../../../Components/Modals/SimilarHouseDesign/SimilarDesign";
import AddPopularDesign from "../../../Components/Modals/PopulaHouseDesign/PopularHouseDesing";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import { useNavigate, useSearchParams } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { IoEye } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

const HouseDesign = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.HouseDesign.CREATE);
  const canDelete = hasPermission(SystemPermissions.HouseDesign.DELETE);
  const canSimilarDesign = hasPermission(SystemPermissions.HouseDesign.SIMILAR_DESIGN);
  const canAddSlideShow = hasPermission(SystemPermissions.HouseDesign.ADD_SLIDE_SHOW);
  const canView = hasPermission(SystemPermissions.HouseDesign.VIEW);
  const canPosition = hasPermission(SystemPermissions.HouseDesign.POSITION);
  const showActions = canSimilarDesign || canView || canDelete;

  const navigate = useNavigate();
  const {
    getHouseDesign,
    addHouseDesign,
    updateHouseDesign,
    deleteHouseDesign,
    searchHouseDesign,
    updateHouseDesignPosition,
  } = useHouseDesign();
  const { getPlans } = useBuyPlans();

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [houseDesignData, setHouseDesignData] = useState([]);
  const [isSimilarDesngModalOpen, setIsSimilarDesngModalOpen] = useState(false);
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [imageError, setImageError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const pageParam = parseInt(searchParams.get("page") || "0", 10);
  const offset = pageParam * limit;
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedDesignForPosition, setSelectedDesignForPosition] = useState(null);
  const [buyPlansList, setBuyPlansList] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    plot_dimension: "",
    covered_area: "",
    floors: 0,
    bed_rooms: 0,
    bath_rooms: 0,
    associated_house: "",
    standard_plan_bands: "",
    customUrl: "",
    description: "",
    servant_quarters: false,
    terrace: false,
    car_parking: false,
    custom_pricing_plan: false,
    selected_buy_plan_ids: [],
    images: [],
    imagePreviews: [],
    primaryImageIndex: 0, // Add primary image index
    // price: 0,
    // name: "",
  });
  useEffect(() => {
    loadHouseDesigns(offset, limit);
  }, [offset]);

  useEffect(() => {
    if (showModal && formData.custom_pricing_plan) {
      getPlans(setBuyPlansList);
    }
  }, [showModal, formData.custom_pricing_plan]);

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / limit);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };
  const closeSimilarDesngModal = () => {
    setIsSimilarDesngModalOpen(false);
  };
  const loadHouseDesigns = async (
    currentOffset = offset,
    currentLimit = limit,
    currentSearchFilter = searchFilter,
  ) => {
    setIsLoading(true);
    try {
      await getHouseDesign(
        setHouseDesignData,
        setTotal,
        currentOffset,
        currentLimit,
        currentSearchFilter,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load house designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "0");
        return next;
      });
      const response = await searchHouseDesign(searchFilter, 0, limit);
      setHouseDesignData(response.data);
      const totalRecords =
        response.pagination?.totalRecords || response.data.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setSearchParams({ page: "0" });
    loadHouseDesigns(0, limit, "");
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };
  const handleQuillChange = (value) => {
    setFormData({
      ...formData,
      description: value,
    });
  };
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newValidImages = [];
    const newImagePreviews = [];

    const checkImageResolution = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width === 1600 && img.height === 900) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            alert(
              `Image "${files[index].name}" does not have the required resolution of 1600*900. Please upload the correct resolution.`,
            );
          }
        });
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex =
            currentImageCount === 0 ? 0 : prev.primaryImageIndex;

          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            primaryImageIndex: newPrimaryIndex,
          };
        });
      },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith("/")) {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if (!formData.images || formData.images.length === 0) {
      setImageError("At least one image is required");
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (
          key !== "image" &&
          key !== "imagePreviews" &&
          key !== "primaryImageIndex" &&
          key !== "selected_buy_plan_ids"
        ) {
          apiData.append(key, formData[key]);
        }
      });

      if (formData.selected_buy_plan_ids?.length > 0) {
        apiData.append(
          "selected_buy_plan_ids",
          JSON.stringify(formData.selected_buy_plan_ids),
        );
      }

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          // Add isPrimary flag for each image
          const isPrimary = index === formData.primaryImageIndex;
          apiData.append(`isPrimary`, isPrimary);
        });
      }

      let response;
      if (isEditing) {
        response = await updateHouseDesign(editId, apiData);
      } else {
        response = await addHouseDesign(apiData);
      }

      await loadHouseDesigns(offset, limit);

      setShowModal(false);
      setIsEditing(false);
      setEditId(null);
      resetForm();

      Swal.fire({
        icon: "success",
        title: isEditing ? "House Design Updated" : "House Design Added",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save house design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }

    setFormData({
      title: "",
      plot_dimension: "",
      covered_area: "",
      floors: 0,
      bed_rooms: 0,
      bath_rooms: 0,
      description: "",
      customUrl: "",
      standard_plan_bands: "",
      custom_pricing_plan: false,
      selected_buy_plan_ids: [],
      servant_quarters: false,
      terrace: false,
      car_parking: false,
      // price: 0,
      name: "",
      images: [],
      imagePreviews: [],
      primaryImageIndex: 0,
    });
  };
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteHouseDesign(id);
        await loadHouseDesigns(offset, limit);
        Swal.fire("Deleted!", "House design deleted successfully.", "success");
      } catch (error) {
        console.error("Failed to delete house design:", error);
        Swal.fire(
          "Error!",
          "Failed to delete house design. Please try again.",
          "error",
        );
      }
    }
  };
  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    const updatedPreviews = [...(formData.imagePreviews || [])];

    if (updatedPreviews[index]) {
      URL.revokeObjectURL(updatedPreviews[index]);
    }

    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);

    // Adjust primary image index if the removed image was primary or before it
    let newPrimaryIndex = formData.primaryImageIndex;
    if (index <= formData.primaryImageIndex) {
      newPrimaryIndex = Math.max(0, formData.primaryImageIndex - 1);
    }

    setFormData({
      ...formData,
      images: updatedImages,
      imagePreviews: updatedPreviews,
      primaryImageIndex: newPrimaryIndex,
    });
    if (updatedImages.length === 0) {
      setImageError("At least one image is required");
    }
  };

  const setPrimaryImage = (index) => {
    setFormData({
      ...formData,
      primaryImageIndex: index,
    });
  };
  const handlePageView = (id) => {
    navigate(`/house-design/${id}`);
  };
  useEffect(() => {
    return () => {
      if (formData.imagePreviews) {
        formData.imagePreviews.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }
    };
  }, [formData.imagePreviews]);
  const handleFeatureChange = (featureName) => {
    setFormData({
      ...formData,
      [featureName]: !formData[featureName],
    });
  };

  const handleCustomPricingPlanChange = () => {
    setFormData({
      ...formData,
      custom_pricing_plan: !formData.custom_pricing_plan,
      ...(formData.custom_pricing_plan ? { selected_buy_plan_ids: [] } : {}),
    });
  };

  const handleBuyPlanCheckboxChange = (planId) => {
    setFormData((prev) => {
      const ids = prev.selected_buy_plan_ids || [];
      const isSelected = ids.includes(planId);
      return {
        ...prev,
        selected_buy_plan_ids: isSelected
          ? ids.filter((id) => id !== planId)
          : [...ids, planId],
      };
    });
  };
  const handleAddSimilarDesign = (id) => {
    setIsSimilarDesngModalOpen(id);
  };

  const handlePositionClick = (design) => {
    setSelectedDesignForPosition({
      recordId: design.id,
      currentPosition: design.position ?? 0,
    });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, newPosition) => {
    await updateHouseDesignPosition(recordId, newPosition);
    await loadHouseDesigns(offset, limit, searchFilter);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedDesignForPosition(null);
  };

  return (
    <div>
      {/* Content */}
      <div className="">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <h3 className="text-xl font-bold">House Design</h3>

          <div className="flex flex-wrap gap-2">
            {canCreate && (
              <button
                onClick={() => {
                  resetForm();
                  setIsEditing(false);
                  setShowModal(true);
                }}
                className="btn btn-primary h-[44px]"
              >
                <FiPlus className="text-white" />
                Add House Design
              </button>
            )}

            {canAddSlideShow && (
              <button
                onClick={() => {
                  setShowPopularDesignModal(true);
                }}
                className="btn btn-primary h-[44px]"
              >
                <FiPlus className="text-white" />
                Add Slide Show
              </button>
            )}
          </div>
        </div>

        {/* Search Filter */}
        <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap md:justify-end items-center gap-3 my-5">
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search house designs"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
            <FiSearch className="search-icon" />
          </div>
          <div className="flex flex-wrap md:flex-nowrap gap-2">
            <button
              className="btn btn-filter filter-btn"
              onClick={handleSearch}
            >
              <FiFilter className="text-white" /> Apply Filter
            </button>
            <button
              className="btn btn-reset-filter filter-btn"
              onClick={handleReset}
            >
              <FiRotateCcw className="text-white" /> Reset Filter
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white w-full rounded-lg pagination-shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
              <thead>
                <tr className="brand-gradient text-white">
                  <th className="table-th">Sr. No.</th>
                  <th className="table-th">Title</th>
                  <th className="table-th">Plot Dimension</th>
                  <th className="table-th">Covered Area</th>
                  {showActions && <th className="table-th">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {houseDesignData.length > 0 ? (
                  houseDesignData.map((design, index) => (
                    <tr
                      key={design.id}
                      className={`${
                        index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                    >
                      <td className="table-td">
                        {canPosition ? (
                          <button
                            type="button"
                            onClick={() => handlePositionClick(design)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                          >
                            {design.position ?? "—"}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-500">
                            {design.position ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="table-td">
                        {design.title || design.name}
                      </td>
                      <td className="table-td">{design.plot_dimension}</td>
                      <td className="table-td">{design.covered_area}</td>
                      {showActions && (
                        <td className="table-td">
                          <div className="flex space-x-2">
                            {/* <button
                            className="bg-blue-500 text-white p-2 rounded-md"
                            onClick={() => handleEdit(design.id)}
                          >
                            <Edit2 size={16} />
                          </button> */}
                            {canSimilarDesign && (
                              <button
                                className="btn btn-action"
                                onClick={() => handleAddSimilarDesign(design.id)}
                              >
                                <FiPlusCircle className="text-white" />
                                Similar Design
                              </button>
                            )}
                            {canView && (
                              <button
                                className="btn btn-view"
                                onClick={() => handlePageView(design.id)}
                              >
                                <IoEye className="text-white" /> View
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn btn-delete text-white"
                                onClick={() => handleDelete(design.id)}
                              >
                                <MdDelete className="text-white" /> Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={showActions ? 5 : 4}
                      className="py-4 px-4 text-center text-gray-500 font-bold"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading house designs...
                        </div>
                      ) : (
                        "No house designs found"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {houseDesignData.length > 0 && total > limit && (
          <div className="mt-4">
            <GlobalPagination
              offset={offset}
              limit={limit}
              total={total}
              onPageChange={handlePageChange}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {/* Add/Edit House Design Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-1/2 relative max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {isEditing ? "Edit House Design" : "Add House Design"}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plot Dimension
                  </label>
                  <input
                    type="text"
                    name="plot_dimension"
                    value={formData.plot_dimension}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Covered Area
                  </label>
                  <input
                    type="text"
                    name="covered_area"
                    value={formData.covered_area}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Floors
                  </label>
                  <input
                    type="number"
                    name="floors"
                    value={formData.floors}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bedrooms
                  </label>
                  <input
                    type="number"
                    name="bed_rooms"
                    value={formData.bed_rooms}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bathrooms
                  </label>
                  <input
                    type="number"
                    name="bath_rooms"
                    value={formData.bath_rooms}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Associate to category
                  </label>
                  <select
                    name="associated_house"
                    value={formData.associated_house}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="3 Marla">3 Marla</option>
                    <option value="5 Marla">5 Marla</option>
                    <option value="8 Marla">8 Marla</option>
                    <option value="10 Marla">10 Marla</option>
                    <option value="1 kanal">1 Kanal</option>
                    <option value="2 kanal">2 Kanal</option>
                    <option value="others">Others</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Standard Plan Bands
                  </label>
                  <select
                    name="standard_plan_bands"
                    value={formData.standard_plan_bands}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">Select Standard Plan Band</option>
                    <option value="Up To 5 Marla">Up To 5 Marla</option>
                    <option value="From 5 to 8 Marla">From 5 to 8 Marla</option>
                    <option value="From 8 to 12 Marla">
                      From 8 to 12 Marla
                    </option>
                    <option value="From 12 to 18 Marla">
                      From 12 to 18 Marla
                    </option>
                    <option value="From 18 to 25 Marla">
                      From 18 to 25 Marla
                    </option>
                    <option value="Above 25 Marla">Above 25 Marla</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Url <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customUrl"
                    value={formData.customUrl}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
              </div>

              {/* Custom Pricing Plan - after Custom URL */}
              <div className="mb-4">
                <div
                  className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer w-fit"
                  onClick={handleCustomPricingPlanChange}
                >
                  <input
                    type="checkbox"
                    id="custom_pricing_plan"
                    name="custom_pricing_plan"
                    checked={formData.custom_pricing_plan}
                    onChange={handleCustomPricingPlanChange}
                    className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                  />
                  <label
                    htmlFor="custom_pricing_plan"
                    className="cursor-pointer select-none"
                    onClick={(e) => e.preventDefault()}
                  >
                    Custom Pricing Plan
                  </label>
                </div>
              </div>

              {formData.custom_pricing_plan && (
                <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Select Buy Plans
                  </h3>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {buyPlansList.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        Loading buy plans...
                      </p>
                    ) : (
                      buyPlansList.map((plan) => {
                        const planId = plan.id;
                        const isChecked = (
                          formData.selected_buy_plan_ids || []
                        ).includes(planId);
                        return (
                          <div
                            key={plan.id}
                            className="flex items-center p-2 rounded border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleBuyPlanCheckboxChange(planId)}
                          >
                            <input
                              type="checkbox"
                              id={`buy-plan-${plan.id}`}
                              checked={isChecked}
                              onChange={() =>
                                handleBuyPlanCheckboxChange(planId)
                              }
                              className="mr-3 h-4 w-4 text-blue-500 focus:ring-blue-400"
                            />
                            <label
                              htmlFor={`buy-plan-${plan.id}`}
                              className="cursor-pointer select-none text-sm flex-1"
                              onClick={(e) => e.preventDefault()}
                            >
                              {plan.name || `Plan #${plan.id}`}
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="border rounded-md">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={handleQuillChange}
                    className="h-40"
                  />
                </div>
              </div>

              {/* Feature Checkboxes */}
              <div className="mb-4">
                <h3 className="mt-12 text-sm font-medium text-gray-700 mb-2">
                  Features
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div
                    className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleFeatureChange("servant_quarters")}
                  >
                    <input
                      type="checkbox"
                      id="servant_quarters"
                      name="servant_quarters"
                      checked={formData.servant_quarters}
                      onChange={() => handleFeatureChange("servant_quarters")}
                      className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                    />
                    <label
                      htmlFor="servant_quarters"
                      className="cursor-pointer select-none"
                      onClick={(e) => e.preventDefault()}
                    >
                      Servant Quarters
                    </label>
                  </div>
                  <div
                    className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleFeatureChange("terrace")}
                  >
                    <input
                      type="checkbox"
                      id="terrace"
                      name="terrace"
                      checked={formData.terrace}
                      onChange={() => handleFeatureChange("terrace")}
                      className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                    />
                    <label
                      htmlFor="terrace"
                      className="cursor-pointer select-none"
                      onClick={(e) => e.preventDefault()}
                    >
                      Terrace
                    </label>
                  </div>
                  <div
                    className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleFeatureChange("car_parking")}
                  >
                    <input
                      type="checkbox"
                      id="car_parking"
                      name="car_parking"
                      checked={formData.car_parking}
                      onChange={() => handleFeatureChange("car_parking")}
                      className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                    />
                    <label
                      htmlFor="car_parking"
                      className="cursor-pointer select-none"
                      onClick={(e) => e.preventDefault()}
                    >
                      Car Parking
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Images <span className="text-lg text-red-500">*</span>
                </label>
                <div className="border border-dashed border-gray-300 p-4 rounded-md relative group">
                  <input
                    type="file"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    id="images"
                    accept="image/*"
                  />
                  <label
                    htmlFor="images"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload size={24} className="text-gray-500 mb-2" />
                    <span className="text-sm text-gray-500">
                      Click to upload multiple images
                    </span>
                  </label>
                  {/* Tooltip */}
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                    <div className="bg-gray-800 text-white text-xs rounded-md py-2 px-3 whitespace-nowrap shadow-lg">
                      Only the image with 1600*900 resolution can be added
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Display selected images */}
              {formData.images.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Selected Images ({formData.images.length})
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {formData.images.map((file, index) => (
                      <div key={index} className="relative">
                        <div
                          className={`h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${
                            index === formData.primaryImageIndex
                              ? "ring-2 ring-blue-500"
                              : ""
                          }`}
                        >
                          {formData.imagePreviews &&
                          formData.imagePreviews[index] ? (
                            <img
                              src={formData.imagePreviews[index]}
                              alt={file.name || `Image ${index + 1}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <p className="text-xs text-gray-500 p-2 truncate">
                              {file.name || "Image " + (index + 1)}
                            </p>
                          )}
                        </div>

                        {/* Primary Image Star */}
                        {index === formData.primaryImageIndex && (
                          <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1">
                            <Star size={12} fill="white" />
                          </div>
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                        >
                          <Trash2 size={12} />
                        </button>

                        {/* Set Primary Button */}
                        {index !== formData.primaryImageIndex && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(index)}
                            className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 transition-colors"
                            title="Set as Primary Image"
                          >
                            <Star size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Primary Image Info */}
                  {formData.images.length > 1 && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs text-blue-700">
                        <Star size={12} className="inline mr-1" />
                        Primary image selected:{" "}
                        {formData.images[formData.primaryImageIndex]?.name ||
                          `Image ${formData.primaryImageIndex + 1}`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-2 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setIsEditing(false);
                    setEditId(null);
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-500 text-white rounded-md flex items-center ${
                    isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : isEditing ? (
                    "Update"
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW: Similar Desing */}
      {isSimilarDesngModalOpen && (
        <SimilarDesignModal
          isOpen={isSimilarDesngModalOpen}
          onClose={closeSimilarDesngModal}
          designId={isSimilarDesngModalOpen}
        />
      )}

      {/* Add Popular Design Modal */}
      {showPopularDesignModal && (
        <AddPopularDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}

      {isPositionModalOpen && selectedDesignForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedDesignForPosition.currentPosition}
          recordId={selectedDesignForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="House Design"
        />
      )}
    </div>
  );
};

export default HouseDesign;
